import React, { useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Icon } from "@/components/ui";
import { useImmTheme, toggleTheme } from "@/components/immersive/theme";
import { track } from "@/lib/track";

const NAV_LINKS = [
  { label: "Home", to: "/" },
  { label: "Services", to: "/home-care-services" },
  { label: "How It Works", to: "/how-it-works" },
  { label: "Senior Care Matching", to: "/senior-care-matching" },
  { label: "FAQs", to: "/faqs" },
  { label: "Book an Appointment", to: "/book-appointment" },
];

const MENU_LINKS = [
  { label: "Home", to: "/", hint: "" },
  { label: "Home Care Services", to: "/home-care-services", hint: "services" },
  { label: "Senior Care Matching", to: "/senior-care-matching", hint: "" },
  { label: "Caregiver Matching", to: "/caregiver-matching", hint: "" },
  { label: "How It Works", to: "/how-it-works", hint: "" },
  { label: "FAQs", to: "/faqs", hint: "" },
  { sep: true },
  { label: "Get Matched", to: "/intake", hint: "families" },
  { label: "Book an Appointment", to: "/book-appointment", hint: "" },
  { sep: true },
  { label: "Become a Partner Agency", to: "/for-agencies", hint: "agencies" },
  { label: "Join as a Caregiver", to: "/caregiver-apply", hint: "caregivers" },
  { label: "Sign in", to: "/login", hint: "" },
  { label: "Staff sign in", to: "/staff", hint: "staff" },
  { sep: true },
  { label: "Contact", to: "/contact", hint: "" },
  { label: "Sitemap", to: "/sitemap", hint: "" },
  { label: "Terms", to: "/terms", hint: "" },
  { label: "Privacy", to: "/privacy", hint: "" },
];

function ThemeToggle() {
  const theme = useImmTheme();
  return (
    <button className="imm-theme-toggle" data-testid="theme-toggle"
      aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} mode`} onClick={toggleTheme}>
      <Icon name={theme === "dark" ? "sun" : "moon"} size={16} />
    </button>
  );
}

/** Persistent, accessible site header: brand + visible primary nav + Get Matched CTA + overflow menu. */
export function HiddenNav() {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);
  const hover = (v) => { clearTimeout(closeTimer.current); if (v) setOpen(true); else closeTimer.current = setTimeout(() => setOpen(false), 260); };
  return (
    <header className="imm-nav imm-wrap" style={{ maxWidth: 1280, marginLeft: "auto", marginRight: "auto" }}>
      <Link className="imm-brand" data-testid="imm-home" to="/" aria-label="HomecareMatches home">
        <img src="/homecarematches-logo.png" alt="HomecareMatches — home care services and senior care matching" />
        <span className="name">HomecareMatches</span>
      </Link>
      <nav className="imm-topnav" aria-label="Primary">
        {NAV_LINKS.map((l) => (
          <Link key={l.to} to={l.to} className="imm-topnav-link" data-testid={`nav-${l.to.replace(/\//g, "") || "home"}`}>{l.label}</Link>
        ))}
      </nav>
      <div className="imm-nav-right">
        <ThemeToggle />
        <Link className="imm-cta imm-cta-sm" to="/intake" data-testid="nav-get-matched"
          onClick={() => track("get_matched_clicked", { from: "header" })}>
          <Icon name="heart" size={16} /> Get Matched Now
        </Link>
        <div className="imm-menu-zone" onMouseEnter={() => hover(true)} onMouseLeave={() => hover(false)}
          onFocus={() => hover(true)} onBlur={() => hover(false)}>
          <button className="imm-menu-toggle" data-testid="imm-menu-toggle" aria-haspopup="true" aria-expanded={open}
            aria-label="Open menu" onClick={() => setOpen((o) => !o)}>
            <span className="imm-menu-dot" /><Icon name="menu" size={18} />
          </button>
          <div className={`imm-menu${open ? " open" : ""}`} role="menu" data-testid="imm-menu">
            {MENU_LINKS.map((l, i) => l.sep ? <div key={i} className="sep" /> : (
              <Link key={i} role="menuitem" tabIndex={open ? 0 : -1} data-testid={`imm-menu-${l.to.replace(/\//g, "") || "home"}`}
                to={l.to} onClick={() => setOpen(false)}>
                {l.label}{l.hint ? <span className="mono">{l.hint}</span> : null}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </header>
  );
}

const FOOTER_GROUPS = [
  { title: "Services", links: [["Home Care Services", "/home-care-services"], ["Senior Care Matching", "/senior-care-matching"], ["In-Home Care", "/home-care-services"], ["Caregiver Matching", "/caregiver-matching"]] },
  { title: "Get Started", links: [["Get Matched", "/intake"], ["Book an Appointment", "/book-appointment"], ["How It Works", "/how-it-works"]] },
  { title: "For Providers", links: [["Become a Partner Agency", "/for-agencies"], ["Join as a Caregiver", "/caregiver-apply"]] },
  { title: "Information", links: [["FAQs", "/faqs"], ["Contact", "/contact"], ["Privacy Policy", "/privacy"], ["Terms of Service", "/terms"], ["Sitemap", "/sitemap"]] },
];

export function ImmersiveFooter() {
  return (
    <footer className="imm-footer">
      <div className="imm-wrap">
        <div className="imm-foot-cols" data-testid="site-footer">
          {FOOTER_GROUPS.map((g) => (
            <div key={g.title} className="imm-foot-col">
              <h4>{g.title}</h4>
              <ul>
                {g.links.map(([label, to], i) => (
                  <li key={i}><Link to={to} data-testid={`foot-${to.replace(/\//g, "")}-${i}`}>{label}</Link></li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <p className="tag" style={{ marginTop: 30 }}>© {new Date().getFullYear()} HomecareMatches — home care services &amp; senior care matching.</p>
      </div>
    </footer>
  );
}

/** Full immersive dark canvas: fixed backdrop, drifting light, grain, hidden nav, footer. */
export function ImmersiveShell({ children, showFooter = true }) {
  const theme = useImmTheme();
  return (
    <div className="imm-root" data-theme={theme} data-testid="immersive-root">
      <div className="imm-light a" aria-hidden="true" />
      <div className="imm-light b" aria-hidden="true" />
      <div className="imm-light c" aria-hidden="true" />
      <div className="imm-abstract" aria-hidden="true" />
      <div className="imm-grain" aria-hidden="true" />
      <div className="imm-content">
        <a href="#main-content" className="skip-link" data-testid="skip-link">Skip to main content</a>
        <HiddenNav />
        <main id="main-content" tabIndex={-1}>{children}</main>
        {showFooter ? <ImmersiveFooter /> : null}
      </div>
    </div>
  );
}

/** Dark chrome for auth: same atmosphere, no hidden nav — the form stays front-and-centre. */
export function AuthBackdrop({ children }) {
  const theme = useImmTheme();
  return (
    <div className="imm-root imm-auth" data-theme={theme} data-testid="immersive-auth">
      <div className="imm-auth-toggle"><ThemeToggle /></div>
      <div className="imm-light a" aria-hidden="true" />
      <div className="imm-light b" aria-hidden="true" />
      <div className="imm-light c" aria-hidden="true" />
      <div className="imm-abstract" aria-hidden="true" />
      <div className="imm-grain" aria-hidden="true" />
      <div className="imm-auth-inner">{children}</div>
    </div>
  );
}

export { Icon };
